import React, { useState, useEffect } from 'react';
import { Modal, ModalBody } from 'reactstrap';
import { getNodeCategory, cleanText } from './LayoutEngine/engine.js';
import { getNodeIndicativeTime } from '../../../services/ExistingVisaTreeService';
import './NodeContentModal.scss';

/**
 * Modal to display and edit node content
 * Clean, minimal UI with bidirectional HTML/WYSIWYG editing
 */

// Icons
const IconCheckmark = () => (
  <svg viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg" width="60" height="60">
    <g fill="none" fillRule="evenodd">
      <circle stroke="#8DDE43" strokeWidth="2" cx="30" cy="30" r="29"/>
      <path d="M43.71 20.29a1 1 0 0 0-1.41 0L23 39.59l-6.29-6.29a1 1 0 0 0-1.41 1.41l7 7a1 1 0 0 0 1.41 0l20-20a1 1 0 0 0 0-1.42z" fill="#8DDE43" fillRule="nonzero"/>
    </g>
  </svg>
);

const WarningIcon = () => (
  <svg viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg" width="60" height="60">
    <g fill="none" fillRule="evenodd">
      <circle stroke="#FFCC00" strokeWidth="2" cx="30" cy="30" r="29"/>
      <text x="30" y="40" textAnchor="middle" fill="#FFCC00" fontSize="36" fontWeight="bold">!</text>
    </g>
  </svg>
);

const ErrorIcon = () => (
  <svg viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg" width="60" height="60">
    <g fill="none" fillRule="evenodd">
      <circle stroke="#E53030" strokeWidth="2" cx="30" cy="30" r="29"/>
      <path d="M38.59 20L30 28.59 21.41 20 20 21.41 28.59 30 20 38.59 21.41 40 30 31.41 38.59 40 40 38.59 31.41 30 40 21.41z" fill="#E53030" fillRule="nonzero"/>
    </g>
  </svg>
);

const NodeContentModal = ({ isOpen, toggle, node, hasChildren }) => {
  const category = node ? getNodeCategory(node, hasChildren) : null;

  const [indicativeTime, setIndicativeTime] = useState(0);

  useEffect(() => {
    if (node && isOpen) {
      getNodeIndicativeTime(node.id)
        .then((result) => {
          const time = result?.indicativeTime ?? 0;
          setIndicativeTime(time);
        })
        .catch((error) => {
          console.error('Error fetching indicative time:', error);
          setIndicativeTime(0);
        });
    }
  }, [node, isOpen, category]);

  if (!node) return null;
  
  const getIcon = () => {
    if (category === 'outcome') {
      const title = cleanText(node.title || '').toLowerCase();
      if (title.includes('eligible') || title.includes('can work')) {
        return <div className="gw__results-success"><IconCheckmark /></div>;
      } else if (title.includes('required') || title.includes('assessment')) {
        return <div className="gw__results-warning"><WarningIcon /></div>;
      } else {
        return <div className="gw__results-failure"><ErrorIcon /></div>;
      }
    }
    return null;
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" className={`gw__node-modal ${category === 'outcome' ? 'result' : 'q-modal'}`} centered scrollable>
      <ModalBody>
        {/* Header */}
        <div className="gw__modal-header">
          <div className="gw__header-left"></div>
          <div className="gw__header-right">
            <span className="gw__node-id">Node ID :{node.id}</span>
            <button className="gw__close-btn" onClick={toggle}>×</button>
          </div>
        </div>

        <div className="gw__modal-content">
          {/* Title */}
          <div className="gw__title-row">
            <h2 className="gw__title">
              {category === 'outcome' 
                ? (cleanText(node.title) || 'Outcome') 
                : 'Question Output'
              }
            </h2>
            {getIcon()}
          </div>

          {category === 'outcome' && (
            <div className="gw__indicative-time-section gw__indicative-time-preview">
              <label>Indicative Time</label>
              <span className="gw__time-value">{indicativeTime} {indicativeTime === 1 ? 'week' : 'weeks'}</span>
            </div>
          )}

          {/* Main Content */}
          <div className="gw__content-section">
            <div className="gw__text-display">
              {node.text ? <span dangerouslySetInnerHTML={{ __html: node.text }} /> : <em>No content</em>}
            </div>
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default NodeContentModal;
